import { Injectable } from '@angular/core';
import { initializeApp, getApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
  Auth
} from 'firebase/auth';
import { environment } from 'src/environments/environment';
import { BackEndService } from './backEnd.service';
// import { BackEndService } from './backEnd.service';

@Injectable({
  providedIn: 'root'
})
export class FirebaseOtpService {
  private static sharedConfirmationResult: ConfirmationResult | null = null;
  private auth: Auth;
  private recaptchaVerifier: RecaptchaVerifier | null = null;

  private currentElementId: string | null = null;

  // Use getter/setter to access shared confirmation result
  private get confirmationResult(): ConfirmationResult | null {
    return FirebaseOtpService.sharedConfirmationResult;
  }

  private set confirmationResult(value: ConfirmationResult | null) {
    FirebaseOtpService.sharedConfirmationResult = value;
  }

  constructor(private backEnd: BackEndService) {
    let app: FirebaseApp;
    try {
      app = getApp();
      console.log('Using existing Firebase app');
    } catch (error) {
      app = initializeApp(environment.firebase);
      console.log('Initialized new Firebase app');
    }
    this.auth = getAuth(app);
  }

  async setupRecaptcha(elementId: string): Promise<void> {
    try {
      console.log('Setting up reCAPTCHA in element:', elementId);
      this.currentElementId = elementId;

      if (this.recaptchaVerifier) {
        this.clearRecaptcha();
      }

      const container = document.getElementById(elementId);
      if (container) {
        container.innerHTML = '';
      } else {
        throw new Error(`Element with ID ${elementId} not found in DOM`);
      }

      this.recaptchaVerifier = new RecaptchaVerifier(this.auth, elementId, {
        size: 'invisible',
        callback: (response: any) => {
          console.log('reCAPTCHA solved');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired - clearing for fresh init');
          this.clearRecaptcha();
        }
      });

      await this.recaptchaVerifier.render();
      console.log('reCAPTCHA initialized successfully');
    } catch (error) {
      this.recaptchaVerifier = null;
      console.error('Error setting up reCAPTCHA:', error);
      throw error;
    }
  }

  async sendOTP(phoneNumber: string, countryCode: string, elementId: string = 'recaptcha-container', isRetry: boolean = false): Promise<boolean> {
    try {
      if (!this.recaptchaVerifier) {
        console.log('reCAPTCHA not initialized, attempting to initialize...');
        await this.setupRecaptcha(elementId);
      }
      if (!this.recaptchaVerifier) {
        throw new Error('Failed to initialize reCAPTCHA. Please check if the element exists.');
      }
      const formattedNumber = phoneNumber.startsWith('+') ? phoneNumber : `${countryCode}${phoneNumber}`;
      this.confirmationResult = await signInWithPhoneNumber(
        this.auth,
        formattedNumber,
        this.recaptchaVerifier
      );
      return true;
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      if (
        !isRetry &&
        (
          error.code === 'auth/captcha-check-failed' ||
          error.code === 'auth/internal-error' ||
          error.code === 'auth/too-many-requests' ||
          error.code === 'auth/quota-exceeded'
        )
      ) {
        console.log(`reCAPTCHA failure (${error.code}), clearing and retrying once...`);
        this.clearRecaptcha();
        await new Promise(resolve => setTimeout(resolve, 800));

        try {
          await this.setupRecaptcha(elementId);
          return await this.sendOTP(phoneNumber, countryCode, elementId, true);
        } catch (retryError: any) {
          console.error('Retry also failed:', retryError);
          throw retryError;
        }
      }
      throw error;
    }
  }

  async verifyOTP(otp: string, metadata?: any): Promise<any> {
    try {
      if (!this.confirmationResult) {
        console.error('No confirmation result available. Check if OTP was sent correctly.');
        throw new Error('No confirmation result available. Please request OTP again.');
      }
      const result = await this.confirmationResult.confirm(otp);
      const idToken = await result.user.getIdToken();
      console.log('Got ID Token, authenticating with backend...');
      const session = await this.backEnd.signInWithOtp(idToken, 'phone', metadata);
      this.clearRecaptcha();
      return { user: result.user, session };
    } catch (error: any) {
      console.error('Error verifying OTP:', error);
      throw error;
    }
  }

  clearRecaptcha(): void {
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
        console.log('reCAPTCHA instance cleared');
      } catch (e) {
        console.warn('Error clearing reCAPTCHA instance:', e);
      }
      this.recaptchaVerifier = null;
    }

    if (this.currentElementId) {
      try {
        const container = document.getElementById(this.currentElementId);
        if (container) {
          container.innerHTML = '';
          console.log(`Cleared HTML for reCAPTCHA element: ${this.currentElementId}`);
        }
      } catch (e) {
        console.warn('Error resetting reCAPTCHA DOM element:', e);
      }
    }
    this.confirmationResult = null;
  }

}
